import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertCircle,
  Trash2,
  ShieldCheck,
  MailCheck,
  Loader2,
  Clock,
  CheckCircle,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/auth";
import { useSelfServiceDeletion } from "@/hooks/profile/useSelfServiceDeletion";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const DeleteAccountSection = () => {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogStep = useRef<1 | 2 | 3>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const {
    status,
    coolingOffUntil,
    deleteRequest,
    refreshStatus,
    pendingEmail,
    hasDeletionRequest,
  } = useSelfServiceDeletion();

  // Handle main click for deletion
  const startDeletion = async () => {
    setIsLoading(true);
    try {
      await deleteRequest();
      toast.success(
        "A confirmation email has been sent. Please check your inbox."
      );
      dialogStep.current = 3; // Move to waiting-for-email step
    } catch (e: any) {
      toast.error(e?.message || "Failed to start account deletion.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancellation of deletion request
  const cancelDeletionRequest = async () => {
    if (!user?.id) return;
    
    setIsCancelling(true);
    try {
      const { error } = await supabase
        .from("data_deletion_requests")
        .update({ status: "cancelled" })
        .eq("user_id", user.id)
        .neq("status", "cancelled");

      if (error) throw error;

      toast.success("Deletion request has been cancelled successfully.");
      await refreshStatus();
    } catch (error: any) {
      console.error("Error cancelling deletion request:", error);
      toast.error("Failed to cancel deletion request. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  // Date formatting
  const formatDate = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;

  // Get status display information
  const getStatusInfo = () => {
    switch (status) {
      case "pending":
        return {
          icon: <MailCheck className="h-5 w-5 text-blue-500" />,
          title: "Pending Email Confirmation",
          message: `Please check your inbox (${pendingEmail}) for a confirmation link to proceed.`,
          color: "blue"
        };
      case "cooling_off":
        return {
          icon: <Clock className="h-5 w-5 text-orange-500" />,
          title: "Cooling-off Period",
          message: `Your account is scheduled for deletion on ${formatDate(coolingOffUntil)}. You may cancel the deletion request before this date.`,
          color: "orange"
        };
      case "ready":
        return {
          icon: <CheckCircle className="h-5 w-5 text-green-500" />,
          title: "Ready for Deletion",
          message: "Your deletion request has been confirmed and your account is ready to be deleted.",
          color: "green"
        };
      case "completed":
        return {
          icon: <CheckCircle className="h-5 w-5 text-gray-500" />,
          title: "Account Deleted",
          message: "Your account has been anonymized and deleted.",
          color: "gray"
        };
      case "cancelled":
        return {
          icon: <AlertCircle className="h-5 w-5 text-gray-500" />,
          title: "Deletion Cancelled",
          message: "Your deletion request was cancelled.",
          color: "gray"
        };
      case "failed":
        return {
          icon: <AlertCircle className="h-5 w-5 text-red-500" />,
          title: "Deletion Failed",
          message: "There was an error processing your deletion request. Please contact support.",
          color: "red"
        };
      default:
        return null;
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Card className="border-destructive/70">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <ShieldCheck className="h-5 w-5 text-destructive" /> Delete Your
          Account
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Step if user has requested deletion already */}
        {hasDeletionRequest && statusInfo ? (
          <div className="space-y-4">
            <div className={cn(
              "p-4 rounded-lg border",
              statusInfo.color === "blue" && "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
              statusInfo.color === "orange" && "bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800",
              statusInfo.color === "green" && "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800",
              statusInfo.color === "red" && "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800",
              statusInfo.color === "gray" && "bg-gray-50 border-gray-200 dark:bg-gray-800/30 dark:border-gray-700"
            )}>
              <div className="flex items-start gap-3">
                {statusInfo.icon}
                <div>
                  <h3 className="font-semibold text-sm dark:text-gray-100">{statusInfo.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 dark:text-gray-300">
                    {statusInfo.message}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="purple"
                size="sm"
                onClick={refreshStatus}
              >
                Refresh Status
              </Button>
              
              {/* Show cancel button during cooling-off period */}
              {status === "cooling_off" && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={cancelDeletionRequest}
                  disabled={isCancelling}
                  className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700 shadow-md"
                >
                  {isCancelling && (
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  )}
                  <X className="h-4 w-4 mr-2" />
                  Cancel Deletion Request
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* User has NOT requested deletion */
          <div>
            <div className="bg-destructive/10 rounded p-3 flex gap-2 items-center border border-destructive/20 mb-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <span>
                <strong>This action is irreversible.</strong> All personal data
                will be anonymized.
                <br />
                However, for democratic transparency, your past votes, comments,
                and participation will remain but will be converted to{" "}
                <em>"Deleted User"</em> in public records.
              </span>
            </div>
            <div className="text-sm text-muted-foreground mb-2">
              We recommend you{" "}
              <span className="underline font-medium text-foreground">
                export your data
              </span>{" "}
              before proceeding.
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      dialogStep.current = 1;
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white shadow-md"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      <Trash2 className="inline-block h-5 w-5 mr-2 text-destructive" />
                      Delete Your Account?
                    </DialogTitle>
                  </DialogHeader>
                  <DialogDescription>
                    Step 1/2: Are you absolutely sure? This will{" "}
                    <strong>anonymize</strong> your profile and personal
                    details. To ensure democratic integrity, your poll
                    participation, votes, comments, and point records will{" "}
                    <span className="underline">
                      remain but will display as "Deleted User"
                    </span>
                    .
                  </DialogDescription>
                  <div className="mt-4 flex flex-col gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        dialogStep.current = 2;
                        setDialogOpen(false);
                        setTimeout(() => setDialogOpen(true), 200); // Progress to step 2
                      }}
                      className="border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                    >
                      Continue
                    </Button>
                    <DialogClose asChild>
                      <Button type="button" variant="ghost">
                        Cancel
                      </Button>
                    </DialogClose>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Step 2: Final irreversible action - request deletion */}
              <Dialog
                open={dialogOpen && dialogStep.current === 2}
                onOpenChange={setDialogOpen}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Final Confirmation</DialogTitle>
                  </DialogHeader>
                  <DialogDescription>
                    Step 2/2: You will be sent a confirmation email before
                    deletion can proceed.
                    <div className={cn("bg-muted border p-2 mt-2 rounded")}>
                      Your personal data (name, email, phone, etc) will be
                      deleted.
                      <br />
                      Your democratic actions will be anonymized as required by
                      GDPR & public transparency.
                    </div>
                    <div className="mt-3 text-sm text-muted-foreground">
                      <ShieldCheck className="inline-block h-4 w-4 mr-1" />
                      <span>
                        To keep our democratic records intact, your actions will
                        be preserved without personal identifiers.
                      </span>
                    </div>
                  </DialogDescription>
                  <DialogFooter className="flex flex-col gap-2 mt-4">
                    <Button
                      className="w-full bg-red-600 hover:bg-red-700 text-white shadow-md"
                      variant="destructive"
                      disabled={isLoading}
                      onClick={async () => {
                        await startDeletion();
                        setDialogOpen(false);
                      }}
                    >
                      {isLoading && (
                        <Loader2 className="animate-spin h-4 w-4 mr-2 inline" />
                      )}
                      Send Confirmation Email & Continue
                    </Button>
                    <DialogClose asChild>
                      <Button type="button" variant="ghost">
                        Back
                      </Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        )}
        
        <div className="text-xs text-muted-foreground mt-4">
          <MailCheck className="inline h-4 w-4 mr-1" />
          For privacy questions, please contact support at{" "}
          <a href="mailto:support@nationcard.org" className="underline">
            support@nationcard.org
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default DeleteAccountSection;
